import * as apiFactory from "./ApiFactory";

import type { BrandSession, GlobalSession, UserSession } from "@/models/UserSession";

export async function getSession<TBrandName extends string>(
  clientName: string | undefined,
  brand: TBrandName | undefined,
): Promise<UserSession<TBrandName>> {
  const searchParams = new URLSearchParams();
  if (clientName != null) {
    searchParams.set("clientName", clientName);
    if (brand != null) searchParams.set("brand", brand);
  }

  const client = await apiFactory.WithBearer();
  return await client.get(`api/my/session${searchParams.size > 0 ? `?${searchParams.toString()}` : ""}`);
}

export async function setGlobalSession(payload: GlobalSession): Promise<void> {
  const client = await apiFactory.WithBearer();
  await client.post("api/my/session", { global: payload });
}

export async function setBrandSession(clientName: string, brand: string, payload: BrandSession): Promise<void> {
  const client = await apiFactory.WithBearer();
  await client.post(`api/my/session?clientName=${clientName}&brand=${brand}`, { [brand]: payload });
}

/**
 * Seeds the brand/case for a not-yet-started Copilot Studio conversation into
 * GlobalSession.agentSessions[conversationId], for UnityAgent.Api's
 * GetSessionState to pick up as a one-time promotion into its own
 * AgentConversationContext — see AgentAssistant.vue. Keyed under "global"
 * rather than a brand, since the agent doesn't know the brand yet at the
 * point it needs to look this up.
 *
 * Spreads the currently-known global session first, matching how
 * saveBrandSession/saveLastUsedBrand always send a full desired object —
 * posting a partial body here would risk clobbering unrelated global keys
 * (e.g. lastUsedBrand) if the backend replaces rather than deep-merges.
 */
export async function setAgentSession(
  conversationId: string,
  clientName: string,
  brand: string,
  caseReference: string,
): Promise<void> {
  const client = await apiFactory.WithBearer();
  const current = await getSession<"global">(undefined, undefined);

  await client.post("api/my/session", {
    global: {
      ...current.global,
      agentSessions: {
        ...(current.global.agentSessions as Record<string, unknown> | undefined),
        [conversationId]: { clientName, brand, caseReference },
      },
    },
  });
}

export async function deleteUserSession(clientName: string, brand: string): Promise<void> {
  const client = await apiFactory.WithBearer();
  await client.delete(["api/my/session", clientName, brand]);
}
